import React, { useState, useRef, ChangeEvent, KeyboardEvent } from 'react';
import './InputMessage.css';
import { Box, Chip, useTheme } from '@mui/material';

// Define the type
type QuestionItem = {
  question: string;
  sector: string;
  color: string;
};


interface InputMessageProps {
  onSendMessage: (message: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  suggestions?: QuestionItem[];
  disabled?: boolean;
  leftSlot?: React.ReactNode;
  topSlot?: React.ReactNode;
}


const InputMessage: React.FC<InputMessageProps> = ({
  onSendMessage,
  onCancel,
  placeholder = "Type your message here...",
  suggestions = [],
  disabled = false,
  leftSlot,
  topSlot,
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const next = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${next}px`;
      // Only show a scrollbar once the content exceeds the max height (150px).
      textareaRef.current.style.overflowY = next > 150 ? 'auto' : 'hidden';
    }
  };

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!disabled) onSendMessage(suggestion);
  };

  const theme = useTheme()

  return (
    <Box className="input-message-container">
      {suggestions.length > 0 && (
        <Box
          className="suggestions-container"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignContent: 'center',
            gap: 0.75,
            padding: 1,
            width: '100%',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <Chip
              key={index}
              size="small"
              style={{ fontSize: '0.82rem', backgroundColor: suggestion.color, color: '#fff', opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto', cursor: disabled ? 'default' : 'pointer' }}
              label={suggestion.question}
              className="suggestion-bubble"
              onClick={() => handleSuggestionClick(suggestion.question)}
              disabled={disabled}
            />
          ))}
        </Box>
      )}


      {topSlot}

      <Box
        className="input-message-wrapper"
        style={{
          backgroundColor: theme.palette.mode === 'dark' ? '#242526' : '#ffffff',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.14)' : '#d9e0e4',
        }}
      >
        {leftSlot}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="input-message-textarea"
          rows={1}
          dir="auto"
          style={{
            color: theme.palette.mode === 'dark' ? '#e6e6e6' : '#1f2d36',
            ['--ph-color' as any]: theme.palette.mode === 'dark' ? '#9aa7b0' : '#5c6d77',
          }}
        />
        {disabled && onCancel ? (
          <button onClick={onCancel} className="stop-button" title="Cancel">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={handleSend}
            className={`send-button ${message.trim() && !disabled ? 'active' : ''}`}
            disabled={!message.trim() || disabled}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </Box>
      <div className="input-disclaimer">
        Enmaeya can make mistakes. Verify important figures against the source reports.
      </div>
    </Box>
  );
};

export default InputMessage;
